import { Box, useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from 'src/auth/AuthContext';
import { PathUtil } from 'src/data/PathUtil';
import apiClient from 'src/service/core/ApiClient';
import { approvalInboxBadgeCount } from '@/data/ApprovalInboxUtil';
import { useApprovalInbox } from '@/pages/approvals/ApprovalInboxContext';
import { TimerProvider } from './timer/TimerContext';
import DesktopHeader from './header/DesktopHeader';
import MobileHeader from './header/MobileHeader';
import MobileNavigationDrawer from './header/MobileNavigationDrawer';
import {
    getMenuNodesForRole,
} from './header/navigation';
import type { HeaderBadgeCounts } from './header/navigation';

const Header = () => {
    const { open: isDrawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuthContext();
    const { summary: approvalInboxSummary } = useApprovalInbox();

    const path = PathUtil.dropSlashesAndId(location.pathname);
    const menuNodes = getMenuNodesForRole(user?.data.role);
    const isLoginPage = path === 'login';
    const isTestEnvironment = apiClient.baseUrl.includes('localhost');
    const approvalBadgeCount = user ? approvalInboxBadgeCount(approvalInboxSummary) : 0;
    const badgeCounts = useMemo<HeaderBadgeCounts>(() => (
        approvalBadgeCount > 0 ? { 'header.nav.approvals': approvalBadgeCount } : {}
    ), [approvalBadgeCount]);

    useEffect(() => {
        if (!location.hash) return;

        const frame = window.requestAnimationFrame(() => {
            document.getElementById(location.hash.slice(1))?.scrollIntoView?.({ behavior: 'smooth' });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [location.hash]);

    const navigateTo = (link: string) => {
        if (link.endsWith('.pdf')) {
            window.open(link, '_blank', 'noopener,noreferrer');
            closeDrawer();
            return;
        }
        navigate(link);
        closeDrawer();
    };

    const handleLogout = () => {
        logout();
        closeDrawer();
        navigate('/');
    };

    return (
        <TimerProvider userId={user?.data.id} organizationId={user?.data.organizationId}>
            <Box
                as='header'
                position='sticky'
                top='0'
                zIndex='sticky'
                px={{ base: '3', md: '4' }}
                bg='bg/90'
                borderBottomWidth='1px'
                borderColor='border.subtle'
                backdropFilter='blur(16px)'
                boxShadow='0 10px 30px -24px rgba(15, 23, 42, 0.65)'
                mb='5'
            >
                <DesktopHeader
                    user={user}
                    menuNodes={menuNodes}
                    badgeCounts={badgeCounts}
                    path={path}
                    isLoginPage={isLoginPage}
                    isTestEnvironment={isTestEnvironment}
                    onNavigate={navigateTo}
                    onLogout={handleLogout}
                />
                <MobileHeader
                    user={user}
                    path={path}
                    isLoginPage={isLoginPage}
                    onOpenDrawer={openDrawer}
                    onNavigate={navigateTo}
                    onLogout={handleLogout}
                />
            </Box>

            <MobileNavigationDrawer
                open={isDrawerOpen}
                user={user}
                menuNodes={menuNodes}
                badgeCounts={badgeCounts}
                path={path}
                onOpenChange={(open) => open ? openDrawer() : closeDrawer()}
                onNavigate={navigateTo}
            />
        </TimerProvider>
    );
};

export default Header;
